import {
  Component,
  signal,
  OnInit,
  OnDestroy,
  computed,
  inject,
  effect,
  PLATFORM_ID,
} from '@angular/core';
import { CommonModule, isPlatformBrowser } from '@angular/common';
import { trigger, style, animate, transition } from '@angular/animations';
import { rxResource } from '@angular/core/rxjs-interop';
import { PopupService } from '@millionshow/api-client';
import { Icon } from '@millionshow/client-libs';
import { SwiperDirective } from '../../directives/swiper.directive';
import type { SwiperOptions } from 'swiper/types';

@Component({
  selector: 'app-popup-banner',
  standalone: true,
  imports: [CommonModule, Icon, SwiperDirective],
  templateUrl: './popup-banner.component.html',
  animations: [
    trigger('slideIn', [
      transition(':enter', [
        style({ transform: 'scale(0.9)', opacity: 0 }),
        animate('300ms ease-out', style({ transform: 'scale(1)', opacity: 1 })),
      ]),
      transition(':leave', [
        animate(
          '200ms ease-in',
          style({ transform: 'scale(0.9)', opacity: 0 })
        ),
      ]),
    ]),
    trigger('slideUp', [
      transition(':enter', [
        style({ transform: 'translateY(100%)' }),
        animate('300ms ease-out', style({ transform: 'translateY(0)' })),
      ]),
      transition(':leave', [
        animate('200ms ease-in', style({ transform: 'translateY(100%)' })),
      ]),
    ]),
  ],
})
export class PopupBannerComponent implements OnInit, OnDestroy {
  private readonly popupService = inject<PopupService>(PopupService);
  private readonly platformId = inject(PLATFORM_ID);
  private readonly storageKey = 'popup_dismissed';

  private get isBrowser(): boolean {
    return isPlatformBrowser(this.platformId);
  }

  private dismissedPopups = signal<Set<string>>(new Set());

  // 노출된 팝업 목록 가져오기 (ALLHOUSE 타입, 최대 3개)
  $popups = rxResource({
    stream: () =>
      this.popupService.popupControllerFindAllV1({ type: 'ALLHOUSE' }),
  });

  // 오늘 하루 보지 않기 처리된 팝업 제외
  visiblePopups = computed(() => {
    const popups = this.$popups.value() || [];
    const dismissed = this.dismissedPopups();
    return popups.filter(
      popup => !dismissed.has(popup.id) && !this.isHiddenForToday(popup.id)
    );
  });

  // 모바일 팝업 스와이퍼 설정 (팝업 개수에 따라 동적 설정)
  mobilePopupSwiperOptions = computed<SwiperOptions>(() => {
    const popupCount = this.visiblePopups().length;
    return {
      slidesPerView: 1,
      spaceBetween: 16,
      loop: popupCount > 1,
      autoplay:
        popupCount > 1
          ? {
              delay: 3000,
              disableOnInteraction: false,
            }
          : false,
      pagination: {
        el: '.swiper-pagination',
        clickable: true,
      },
    };
  });

  // 팝업이 열려있을 때 body 스크롤 방지
  private scrollLockEffect = effect(() => {
    if (!this.isBrowser) {
      return;
    }

    if (this.visiblePopups().length > 0) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
  });

  ngOnInit(): void {
    if (!this.isBrowser) {
      return;
    }

    // 초기화 시 오늘 하루 보지 않기 목록 로드
    this.loadDismissedPopups();
  }

  ngOnDestroy(): void {
    if (!this.isBrowser) {
      return;
    }

    // 컴포넌트 파괴 시 스크롤 복원
    document.body.style.overflow = '';
  }

  private loadDismissedPopups(): void {
    if (!this.isBrowser) {
      return;
    }

    const today = this.getTodayString();
    const dismissed = new Set<string>();

    // localStorage에서 오늘 날짜에 숨긴 팝업들 확인
    const storedData = localStorage.getItem(this.storageKey);
    if (storedData) {
      try {
        const parsed = JSON.parse(storedData);
        if (parsed.date === today) {
          parsed.ids.forEach((id: string) => dismissed.add(id));
        } else {
          // 날짜가 다르면 초기화
          localStorage.removeItem(this.storageKey);
        }
      } catch {
        localStorage.removeItem(this.storageKey);
      }
    }

    this.dismissedPopups.set(dismissed);
  }

  private isHiddenForToday(popupId: string): boolean {
    if (!this.isBrowser) {
      return false;
    }

    const today = this.getTodayString();
    const storedData = localStorage.getItem(this.storageKey);

    if (storedData) {
      try {
        const parsed = JSON.parse(storedData);
        if (parsed.date === today) {
          return parsed.ids.includes(popupId);
        }
      } catch {
        return false;
      }
    }

    return false;
  }

  closePopup(popupId: string): void {
    const dismissed = new Set(this.dismissedPopups());
    dismissed.add(popupId);
    this.dismissedPopups.set(dismissed);
  }

  closeAllPopups(): void {
    const allPopupIds = this.visiblePopups().map(p => p.id);
    const dismissed = new Set(this.dismissedPopups());
    allPopupIds.forEach(id => dismissed.add(id));
    this.dismissedPopups.set(dismissed);
  }

  hideForToday(popupId: string): void {
    if (!this.isBrowser) {
      return;
    }

    const today = this.getTodayString();

    // localStorage에 저장
    let storedData = { date: today, ids: [] as string[] };
    const existing = localStorage.getItem(this.storageKey);

    if (existing) {
      try {
        const parsed = JSON.parse(existing);
        if (parsed.date === today) {
          storedData = parsed;
        }
      } catch {
        // ignore parse errors
      }
    }

    if (!storedData.ids.includes(popupId)) {
      storedData.ids.push(popupId);
    }

    localStorage.setItem(this.storageKey, JSON.stringify(storedData));

    // UI에서도 제거
    this.closePopup(popupId);
  }

  hideAllForToday(): void {
    const allPopupIds = this.visiblePopups().map(p => p.id);
    allPopupIds.forEach(id => this.hideForToday(id));
  }

  private getTodayString(): string {
    return new Date().toISOString().split('T')[0];
  }
}
